import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import axiosInstance from "../../utils/axiosInstance";

const AirbnbImages = ({ airbnbId }) => {
  const [images, setImages] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const fetchImages = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/airbnbs/${airbnbId}/images`);
      setImages(res.data || []);
    } catch (err) {
      console.error("Failed to load images:", err);
    } finally {
      setLoading(false);
    }
  }, [airbnbId]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const handleUpload = async () => {
    if (!files.length) return;
    try {
      setUploading(true);
      const uploads = Array.from(files).map(async (file) => {
        const formData = new FormData();
        formData.append("image", file);
        return axiosInstance.post(`/airbnbs/${airbnbId}/images`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      });
      await Promise.all(uploads);
      setFiles([]);
      fetchImages();
    } catch (err) {
      console.error("Upload failed:", err);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm("Delete this image?")) return;
    try {
      await axiosInstance.delete(`/airbnbs/${airbnbId}/images/${imageId}`);
      setImages((prev) => prev.filter((img) => img.id !== imageId));
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Images
      </Typography>

      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => setFiles(e.target.files)}
        />
        <Button
          variant="contained"
          disabled={!files.length || uploading}
          onClick={handleUpload}
        >
          {uploading ? "Uploading..." : "Upload"}
        </Button>
      </Box>

      {uploading && <LinearProgress sx={{ mt: 1, mb: 1, width: "200px" }} />}

      {/* Preview selected files before upload */}
      {files.length > 0 && (
        <Box display="flex" gap={2} mt={2} flexWrap="wrap">
          {Array.from(files).map((file, idx) => (
            <Box
              key={idx}
              sx={{
                width: 100,
                border: "1px dashed #ccc",
                borderRadius: 2,
                p: 1,
                textAlign: "center",
              }}
            >
              <img
                src={URL.createObjectURL(file)}
                alt="preview"
                width="100%"
                height="80"
                style={{ objectFit: "cover", borderRadius: 4 }}
              />
              <Typography variant="caption" sx={{ display: "block" }}>
                {file.name.slice(0, 10)}...
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {loading ? (
        <CircularProgress sx={{ mt: 2 }} />
      ) : (
        <Box display="flex" gap={2} mt={2} flexWrap="wrap">
          {images.map((img) => (
            <Box
              key={img.id}
              textAlign="center"
              sx={{
                border: "1px solid #ddd",
                borderRadius: 2,
                p: 1,
                width: 120,
              }}
            >
              <img
                src={img.image_url}
                alt="Airbnb"
                width="100%"
                height="80"
                style={{ objectFit: "cover", borderRadius: 4 }}
              />
              <Button
                size="small"
                color="error"
                onClick={() => handleDelete(img.id)}
                sx={{ mt: 1 }}
              >
                Delete
              </Button>
            </Box>
          ))}
          {!images.length && (
            <Typography variant="body2" color="text.secondary">
              No images uploaded yet.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AirbnbImages;
